/**
 * SOLID Review
 * S — Strong. sole job: implement IAdapterApolloRoom's room-lifecycle/subscription operations against the
 *     real backend via Apollo's imperative client API. Strong for the methods it currently has
 *     (leaveRoom — Phase 1b Group A; subscribeToPlayerSuspended/subscribeToPlayerResumed/
 *     subscribeToRoomAbandoned/subscribeToDecisionCountdown — Phase 1b Group B; getRoomByCode/
 *     getActiveRoomForPlayer — Group C2, via `client.query()`; joinRoom/lateJoinRoom/
 *     rejoinRoomByCode — Group C3, via `client.mutate()`, completing the smart-join routing chain;
 *     getRoom/subscribeToRoomUpdated — Phase 2 Group B). createRoom/startGame were deleted as
 *     housekeeping — fabricated placeholders with zero known callers anywhere in the tree,
 *     confirmed after Phase 2 completed. getLiveKitToken/markMediaPresence/
 *     reportLiveKitConnectionState/getLiveKitHealth/getLiveKitUsageReport (Phase 1b Group A,
 *     Phase 2 Groups C/E) moved onto a real IAdapterApolloLiveKit port
 *     (infrastructure/livekit/ApolloLiveKitAdapter.ts) as part of the ISP fix
 *     (plan/plan_frontend_makeover.md) — they were LiveKit concerns co-located here, not
 *     room-lifecycle operations.
 * O — Good. A new room-lifecycle operation is a new method here plus a new IAdapterApolloRoom signature —
 *     mechanical, no existing method touched.
 * L — N/A — no inheritance hierarchy; implements IAdapterApolloRoom directly.
 * I — Strong. Implements exactly the room-lifecycle/subscription interface it needs, nothing else — no
 *     longer bundled alongside LiveKit signaling/admin operations on a shared port.
 * D — Strong. Takes its domain's GraphQL port and the logger as constructor-injected dependencies rather than
 *     importing a singleton — testable without a real network; its calls go through `GraphQLCall` (§28.288).
 *     The 4 subscribe methods deliberately relay raw server events
 *     unfiltered — the room-scoping defense-in-depth guard for playerSuspended/
 *     playerResumed/roomAbandoned stays in AppRoot.tsx, unchanged from before this migration (see
 *     plan/plan_frontend_makeover.md's Phase 1b Group B entry for why). joinRoom/lateJoinRoom/
 *     rejoinRoomByCode are equally thin — the REJOIN identity-swap logic (adopting the
 *     server-authoritative playerId on a suspended-slot match) stays in AppRoot.tsx too, operating on this
 *     adapter's plain `Result`-unwrapped response.
 */
import { gql } from '@apollo/client';
import type { Result } from '../../shared/Result';
import type { ICommandLeaveRoom, IEntityRoomCodeLookup, IEntityActiveRoomForPlayer, IEntityRawRoom, IEntityRejoinRoomByCode } from '../../application/ports/apolloShapes';
import type { RoomId, PlayerId } from '../../application/ports/ClientVocabulary';
import type { IAdapterApolloRoom } from '../../application/ports/IAdapterApolloRoom';
import type { IPortGraphQLDomainClient } from '../contract/ports';
import type { IServiceLogger } from '../platform/IServiceLogger';
import { GraphQLCall } from './GraphQLCall';
import type { IEntityTableView } from '../../application/ports/IAdapterApolloRoom';

// ---------------------------------------------------------------------------
// GraphQL operations (Group A). Moved here from AppRoot.tsx's inline definitions.
// ---------------------------------------------------------------------------

const LEAVE_ROOM_MUTATION = gql`
  mutation ApolloRoomAdapter_LeaveRoom($roomId: ID!, $playerId: ID!) {
    leaveRoom(roomId: $roomId, playerId: $playerId)
  }
`;

const PLAYER_SUSPENDED_SUB = gql`
  subscription ApolloRoomAdapter_PlayerSuspended($roomId: ID!) {
    playerSuspended(roomId: $roomId) { playerId roomId isSuspended }
  }
`;

const PLAYER_RESUMED_SUB = gql`
  subscription ApolloRoomAdapter_PlayerResumed($roomId: ID!) {
    playerResumed(roomId: $roomId) { playerId roomId isSuspended }
  }
`;

const ROOM_ABANDONED_SUB = gql`
  subscription ApolloRoomAdapter_RoomAbandoned($roomId: ID!) {
    roomAbandoned(roomId: $roomId) { roomId reason }
  }
`;

const DECISION_COUNTDOWN_SUB = gql`
  subscription ApolloRoomAdapter_DecisionCountdown($roomId: ID!) {
    decisionCountdown(roomId: $roomId) { roundId challengerId secondsRemaining }
  }
`;

const TABLE_VIEW_SUB = gql`
  subscription ApolloRoomAdapter_TableView($roomId: ID!) {
    tableView(roomId: $roomId) {
      roomId version status suspendedPlayerIds
      activeRound {
        roundId rollerId diceRolled minimumScore
        pendingClaim { claimedScore label challengerId wasBlind }
        playerLives { playerId lives }
        revealResult { losingPlayerId wasBluff claimedScore trueScore die1 die2 newRollerId endsGame loserLivesLeft }
      }
    }
  }
`;

const GET_ROOM_BY_CODE_QUERY = gql`
  query ApolloRoomAdapter_GetRoomByCode($code: String!) {
    getRoomByCode(code: $code) {
      roomId roomStatus playerCount
      suspendedPlayers { id displayName email isConnected }
    }
  }
`;

const GET_ACTIVE_ROOM_FOR_PLAYER_QUERY = gql`
  query ApolloRoomAdapter_GetActiveRoomForPlayer($playerId: ID!) {
    getActiveRoomForPlayer(playerId: $playerId) {
      roomId roomCode roomStatus playerCount
    }
  }
`;

const CREATE_ROOM_MUTATION = gql`
  mutation ApolloRoomAdapter_CreateRoom($hostPlayerId: ID!) {
    createRoom(hostPlayerId: $hostPlayerId) {
      id roomCode playerIds
      players { id displayName }
      status
    }
  }
`;

const JOIN_ROOM_MUTATION = gql`
  mutation ApolloRoomAdapter_JoinRoom($roomId: ID!, $playerId: ID!) {
    joinRoom(roomId: $roomId, playerId: $playerId) {
      id roomCode playerIds players { id displayName } status
    }
  }
`;

const LATE_JOIN_ROOM_MUTATION = gql`
  mutation ApolloRoomAdapter_LateJoinRoom($roomId: ID!, $playerId: ID!, $playerDisplayName: String!, $playerMatchCredits: Int) {
    lateJoinRoom(roomId: $roomId, playerId: $playerId, playerDisplayName: $playerDisplayName, playerMatchCredits: $playerMatchCredits) {
      id roomCode playerIds players { id displayName } status
    }
  }
`;

const REJOIN_BY_CODE_MUTATION = gql`
  mutation ApolloRoomAdapter_RejoinByCode($roomCode: String!, $playerId: ID!, $playerDisplayName: String!, $playerEmail: String) {
    rejoinRoomByCode(roomCode: $roomCode, playerId: $playerId, playerDisplayName: $playerDisplayName, playerEmail: $playerEmail) {
      playerId roomId roomCode roomStatus players { id displayName }
    }
  }
`;

const GET_ROOM_QUERY = gql`
  query ApolloRoomAdapter_GetRoom($id: ID!) {
    room(id: $id) {
      id roomCode playerIds players { id displayName } status
    }
  }
`;

const ROOM_UPDATED_SUB = gql`
  subscription ApolloRoomAdapter_RoomUpdated($roomId: ID!) {
    roomUpdated(roomId: $roomId) {
      id roomCode playerIds players { id displayName } status
    }
  }
`;

export class ApolloRoomAdapter implements IAdapterApolloRoom {
  private readonly call: GraphQLCall;

  // The executor arrives from this domain's socket on ComponentFrontendInfrastructure, not as
  // the raw ApolloClient. That socket is the single path from "this app can reach the domain"
  // to "this adapter executes" — disconnect it and this adapter degrades to the Null client
  // instead of quietly using a module singleton (§28.72).
  constructor(client: IPortGraphQLDomainClient, logger: IServiceLogger) {
    this.call = new GraphQLCall('ApolloRoomAdapter', client, logger);
  }

  async getRoom(roomId: RoomId): Promise<Result<IEntityRawRoom>> {
    return this.call.required('getRoom', 'fetching room', (apollo) => apollo.query<{ room: IEntityRawRoom | null }>({
      query: GET_ROOM_QUERY,
      variables: { id: roomId },
      fetchPolicy: 'network-only',
    }), (data) => data.room);
  }

  async getRoomByCode(code: string): Promise<Result<IEntityRoomCodeLookup | null>> {
    return this.call.optional('getRoomByCode', 'looking up room by code', (apollo) => apollo.query<{ getRoomByCode: IEntityRoomCodeLookup | null }>({
      query: GET_ROOM_BY_CODE_QUERY,
      variables: { code },
      fetchPolicy: 'network-only',
    }), (data) => data.getRoomByCode);
  }

  async getActiveRoomForPlayer(playerId: PlayerId): Promise<Result<IEntityActiveRoomForPlayer | null>> {
    return this.call.optional('getActiveRoomForPlayer', 'looking up active room for player', (apollo) => apollo.query<{ getActiveRoomForPlayer: IEntityActiveRoomForPlayer | null }>({
      query: GET_ACTIVE_ROOM_FOR_PLAYER_QUERY,
      variables: { playerId },
      fetchPolicy: 'network-only',
    }), (data) => data.getActiveRoomForPlayer);
  }

  /**
   * MOVED OUT OF MainMenuPanel.tsx (2026-08-30), where it was an inline `gql` plus a `useMutation`.
   * IAdapterApolloRoom's own comment recorded that the real flow went "entirely bypassing this
   * port" — a live bypass described rather than fixed. The document is byte-identical apart from
   * the operation name, which follows this adapter's convention.
   */
  async createRoom(hostPlayerId: PlayerId): Promise<Result<IEntityRawRoom>> {
    return this.call.required('createRoom', 'creating room', (apollo) => apollo.mutate<{ createRoom: IEntityRawRoom }>({
      mutation: CREATE_ROOM_MUTATION,
      variables: { hostPlayerId },
    }), (data) => data.createRoom);
  }

  async joinRoom(roomId: RoomId, playerId: PlayerId): Promise<Result<IEntityRawRoom>> {
    return this.call.required('joinRoom', 'joining room', (apollo) => apollo.mutate<{ joinRoom: IEntityRawRoom }>({
      mutation: JOIN_ROOM_MUTATION,
      variables: { roomId, playerId },
    }), (data) => data.joinRoom);
  }

  async lateJoinRoom(roomId: RoomId, playerId: PlayerId, playerDisplayName: string, playerMatchCredits: number): Promise<Result<IEntityRawRoom>> {
    return this.call.required('lateJoinRoom', 'late-joining room', (apollo) => apollo.mutate<{ lateJoinRoom: IEntityRawRoom }>({
      mutation: LATE_JOIN_ROOM_MUTATION,
      variables: { roomId, playerId, playerDisplayName, playerMatchCredits },
    }), (data) => data.lateJoinRoom);
  }

  async rejoinRoomByCode(roomCode: string, playerId: PlayerId, playerDisplayName: string, playerEmail: string | null): Promise<Result<IEntityRejoinRoomByCode>> {
    return this.call.required('rejoinRoomByCode', 'rejoining room by code', (apollo) => apollo.mutate<{ rejoinRoomByCode: IEntityRejoinRoomByCode }>({
      mutation: REJOIN_BY_CODE_MUTATION,
      variables: { roomCode, playerId, playerDisplayName, playerEmail: playerEmail ?? undefined },
    }), (data) => data.rejoinRoomByCode);
  }

  async leaveRoom(command: ICommandLeaveRoom): Promise<Result<void>> {
    return this.call.done('leaveRoom', 'leaving room', (apollo) => apollo.mutate({
      mutation: LEAVE_ROOM_MUTATION,
      variables: { roomId: command.roomId, playerId: command.playerId },
    }));
  }

  subscribeToPlayerSuspended(roomId: RoomId, onEvent: (ev: { playerId: PlayerId; roomId: RoomId }) => void): () => void {
    return this.call.subscribe('playerSuspended', (apollo) => apollo.subscribe<{ playerSuspended: { playerId: PlayerId; roomId: RoomId; isSuspended: boolean } }>({ query: PLAYER_SUSPENDED_SUB, variables: { roomId } }),
      (data) => data.playerSuspended, onEvent);
  }

  subscribeToPlayerResumed(roomId: RoomId, onEvent: (ev: { playerId: PlayerId; roomId: RoomId }) => void): () => void {
    return this.call.subscribe('playerResumed', (apollo) => apollo.subscribe<{ playerResumed: { playerId: PlayerId; roomId: RoomId; isSuspended: boolean } }>({ query: PLAYER_RESUMED_SUB, variables: { roomId } }),
      (data) => data.playerResumed, onEvent);
  }

  subscribeToRoomAbandoned(roomId: RoomId, onEvent: (ev: { roomId: RoomId; reason: string }) => void): () => void {
    return this.call.subscribe('roomAbandoned', (apollo) => apollo.subscribe<{ roomAbandoned: { roomId: RoomId; reason: string } }>({ query: ROOM_ABANDONED_SUB, variables: { roomId } }),
      (data) => data.roomAbandoned, onEvent);
  }

  subscribeToDecisionCountdown(roomId: RoomId, onEvent: (ev: { roundId: string; challengerId: string; secondsRemaining: number }) => void): () => void {
    return this.call.subscribe('decisionCountdown', (apollo) => apollo.subscribe<{ decisionCountdown: { roundId: string; challengerId: string; secondsRemaining: number } }>({ query: DECISION_COUNTDOWN_SUB, variables: { roomId } }),
      (data) => data.decisionCountdown, onEvent);
  }

  subscribeToTableView(roomId: RoomId, onEvent: (view: IEntityTableView) => void): () => void {
    return this.call.subscribe('tableView', (apollo) => apollo.subscribe<{ tableView: IEntityTableView }>({ query: TABLE_VIEW_SUB, variables: { roomId } }),
      (data) => data.tableView, onEvent);
  }

  subscribeToRoomUpdated(roomId: RoomId, onEvent: (ev: IEntityRawRoom) => void): () => void {
    return this.call.subscribe('roomUpdated', (apollo) => apollo.subscribe<{ roomUpdated: IEntityRawRoom }>({ query: ROOM_UPDATED_SUB, variables: { roomId } }),
      (data) => data.roomUpdated, onEvent);
  }

}
